import React, { useContext, useEffect } from "react";
import { TestContext } from "../../context/test_context";
import {
  alpha,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Switch,
  Typography,
  useTheme
} from "@mui/material";
import { TestCard } from "../../components/global/VideoCard";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import { VideoContext } from "../../context/video_context";
import TestOptions from "../../components/global/TestOptions";
import usePayment from "../../hooks/payment_hook";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import { localizedTimeToTest } from "../../utils/time_calculators";

export default function Settings(): any {
  const { tests, loading } = React.useContext(TestContext);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const {
    hasPaid,
    isCheckingPayment,
    getUserSubscriptionSettings,
    subscriptionSettings,
    isLoadingSubscriptionSettings
  } = usePayment();

  useEffect(() => {
    if (hasPaid && !isCheckingPayment) {
      getUserSubscriptionSettings();
    }
  }, [hasPaid, isCheckingPayment]);

  return (
    <Box
      sx={{
        width: "100%",
        overflowY: "auto",
        paddingX: 6,
        paddingTop: 2
      }}
    >
      <Box sx={{ fontSize: "h2.fontSize" }}>Settings</Box>
      {loading || isCheckingPayment ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%"
          }}
        >
          <CircularProgress></CircularProgress>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            paddingTop: "20px",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            gap: 4
          }}
        >
          {/* Membership Settings Section */}
          <Box
            sx={{
              boxShadow: `0px 0px 5px 0px ${alpha("#000000", 0.3)}`,
              borderRadius: 2
            }}
          >
            <Box
              sx={{
                fontSize: "h3.fontSize",
                boxShadow: `0px 2px 2px 0px ${alpha("#000000", 0.1)}`,
                margin: 0,
                padding: 2
              }}
            >
              Membership
            </Box>
            <Box
              sx={{
                padding: 2
              }}
            >
              <Box
                className="flex flex-col items-center justify-center mb-4 w-full"
              >
                {hasPaid ? (
                  <Box className="flex flex-col items-center justify-center w-full ">
                    {isLoadingSubscriptionSettings ? (
                      <Box className="flex justify-center my-2">
                        <CircularProgress size={24} />
                      </Box>
                    ) : subscriptionSettings ? (
                      <Box className={"flex flex-row justify-between items-center w-full"}>
                        <Box>
                          <Typography variant="h5" sx={{ mb: 1 }}>
                            Upcoming Renewal:
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 3 }}>
                            {localizedTimeToTest(subscriptionSettings.renews_at)}
                          </Typography>
                          <Typography variant="h5" sx={{ mb: 1 }}>
                            Your Plan:
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 3 }}>
                            {subscriptionSettings.product_name}
                          </Typography>
                          <Typography variant="h5" sx={{ mb: 1 }}>
                            Status:
                          </Typography>
                          <Typography variant="h5" className="text-green-500" sx={{ mb: 3 }}>
                            {subscriptionSettings.status}
                          </Typography>
                        </Box>
                        <Box className="flex flex-col items-center justify-center w-1/2">
                          <Box className="flex flex-col items-center justify-center w-[70%] gap-8">
                            <Button
                              variant="contained"
                              color="primary"
                              size="medium"
                              fullWidth
                              onClick={() => {
                                if (subscriptionSettings.customer_portal_url) {
                                  window.open(subscriptionSettings.customer_portal_url, "_blank");
                                }
                              }}
                              sx={{
                                py: 1
                              }}
                            >
                              Manage Subscription
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              size="medium"
                              fullWidth
                              onClick={() => {
                                if (subscriptionSettings?.update_payment_method_url) {
                                  window.open(subscriptionSettings.update_payment_method_url, "_blank");
                                }
                              }}
                              sx={{
                                py: 1
                              }}
                            >
                              Update Payment Method
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              size="medium"
                              fullWidth
                              onClick={() => {
                                if (subscriptionSettings?.customer_portal_update_subscription_url) {
                                  window.open(subscriptionSettings.customer_portal_update_subscription_url, "_blank");
                                }
                              }}
                              sx={{
                                py: 1
                              }}
                            >
                              Change Plan
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={getUserSubscriptionSettings}
                        sx={{
                          py: 1
                        }}
                      >
                        Something went wrong, click here to load subscription management again.
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Box className="w-full flex flex-col items-center justify-center">
                    <Alert
                      severity="info"
                      className="w-full mb-3"
                    >
                      You don't have an active subscription
                    </Alert>
                    <Typography variant="h4" sx={{ mb: 2 }}>
                      Free Account
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Upgrade to premium to access all features and unlimited tests.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={() => navigate("/checkout")}
                      sx={{
                        py: 1.5,
                        mb: 2
                      }}
                    >
                      Upgrade to Premium
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Default Test Settings Section */}
          <Box
            sx={{
              boxShadow: `0px 0px 5px 0px ${alpha("#000000", 0.3)}`,
              borderRadius: 2
            }}
          >
            <Box
              sx={{
                fontSize: "h3.fontSize",
                boxShadow: `0px 2px 2px 0px ${alpha("#000000", 0.1)}`,
                margin: 0,
                padding: 2
              }}
            >
              Default Test Settings
            </Box>
            <Box
              sx={{
                padding: 2
              }}
            >
              <TestOptions />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}